import { Typography } from '@/components/common';
import { InputList, InputListMultiple, InputText } from '@/components/form';
import React from 'react';
import { ContainerInputs } from './ContainerInputs';
import { CaseContext } from '@/context/CaseContext';
import { rules } from './rules';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { CaseDetailsLawyer, Option } from '@/interfaces';
import { useClaimIcons } from '@/hooks';
import {
	casesService,
	categoriesService,
	GET_CASE_TYPES,
	GET_CASES_STATUS,
	GET_CATEGORIES,
} from '@/api';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import { DEFAULT_CASE_NAME } from './consts';

interface CaseBasicInformationProps {
	caseDetails?: CaseDetailsLawyer;
	errors?: any;
	applyRule: boolean;
	listStates: Option[] | undefined;
	isPublished?: boolean;
}
const CaseBasicInformation: React.FC<CaseBasicInformationProps> = ({
	caseDetails,
	applyRule,
	listStates,
	isPublished,
}) => {
	const {
		register,
		setValue,
		trigger,
		formState: { errors },
	} = useFormContext();
	const width = useDeviceWidth();
	const { dirIcons } = useClaimIcons();
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const [allStatesSelected, setAllStatesSelected] = React.useState(false);

	const isDisabled = isCaseReadOnly || isPublished;

	// Categories
	const { data: categoriesData } = useQuery({
		queryKey: [GET_CATEGORIES],
		queryFn: () => categoriesService.getAll(),
	});

	// Types
	const { data: types } = useQuery({
		queryKey: [GET_CASE_TYPES],
		queryFn: () => casesService.getTypes(),
	});
	// Status
	const { data: statusData } = useQuery({
		queryKey: [GET_CASES_STATUS],
		queryFn: () => casesService.getCasesStatus(),
	});

	// Set title
	React.useEffect(() => {
		if (caseDetails)
			handleSetValue(
				'name',
				caseDetails?.title === DEFAULT_CASE_NAME ? '' : caseDetails?.title || ''
			);
	}, [caseDetails]);

	const listCategories = React.useMemo(() => {
		const list: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (categoriesData?.data) {
			categoriesData.data.forEach((category) => {
				list.push({
					label: category.name,
					name: category.id.toString(),
					icon: dirIcons[
						category.name
							.toLocaleLowerCase()
							.replace(' ', '_') as keyof typeof dirIcons
					],
				});
			});
		}

		return list;
	}, [categoriesData]);

	const initialCategory = React.useMemo(() => {
		return listCategories.find(
			({ name }) => String(caseDetails?.category_id) === name
		);
	}, [listCategories, caseDetails?.category_id]);

	const listCaseTypes = React.useMemo(() => {
		const list: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (types?.data.items) {
			types.data.items.forEach((type) => {
				list.push({
					label: type.name,
					name: type.id.toString(),
				});
			});
		}

		return list;
	}, [types]);

	const initialCaseType = React.useMemo(() => {
		return listCaseTypes?.find(({ name }) => caseDetails?.case_type === name);
	}, [listCaseTypes, caseDetails?.case_type]);

	const listCaseStatus = React.useMemo(() => {
		const listStatus: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (statusData?.data) {
			statusData.data.forEach((status) => {
				listStatus.push({
					label: status.name,
					name: status.id.toString(),
				});
			});
		}

		// Sort the list based on name (assuming `name` is numeric or can be converted)
		return listStatus.sort((a, b) => Number(a.name) - Number(b.name));
	}, [statusData]);

	const initialCaseStatus = React.useMemo(() => {
		return listCaseStatus.find(
			({ name }) => String(caseDetails?.status_id) === name
		);
	}, [listCaseStatus, caseDetails?.status_id]);

	const initialListStates = React.useMemo(() => {
		if (!caseDetails?.states) return [];

		return Array.from(
			new Set(
				caseDetails.states
					.map((state) => String(state.id))
					.filter((id) => id !== 'null')
			)
		);
	}, [caseDetails]);

	const handleSetValue = (name: string, value: any) => {
		setValue(name, value, {
			shouldDirty: true,
		});
	};
	const handleSelectAllStates = (selected: boolean) => {
		setAllStatesSelected(selected);
		if (selected) {
			const allStateIds =
				listStates
					?.filter((state) => state.name !== '' && state.name !== 'all')
					.map((state) => state.name) || [];
			setValue('elegibleStateIds', allStateIds);
		} else {
			setValue('elegibleStateIds', []);
		}
		trigger('elegibleStateIds');
	};

	return (
		<>
			<div>
				<Typography type="semibold-20" className="!text-[20px]">
					Basic Information
				</Typography>
			</div>
			<div>
				<InputText
					disabled={isDisabled}
					placeholder="No more than 4 words"
					classNameInput="!mt-2 !text-[16px] placeholder:!text-[16px]"
					classNameTitle="!text-[16px]"
					name="name"
					title="Name"
					register={register}
					rules={rules.name}
					error={errors?.name}
				/>
			</div>
			<ContainerInputs>
				<div className="flex-1">
					{listCategories && (
						<InputList
							// disabled={isCaseReadOnly || !caseDetails?.is_draft}
							disabled={isDisabled}
							myDefaultValue={initialCategory?.name}
							name="category"
							title="Category"
							placeholder="Select"
							classNameTitle="!text-[16px]"
							register={register}
							classNameInput="!mt-2"
							classNameLabel="!text-[16px]"
							options={listCategories}
							setValueInput={handleSetValue}
							rules={applyRule ? rules.category : undefined}
							error={applyRule ? errors?.category : undefined}
						/>
					)}
				</div>
				<div className="flex-1 w-full lg:w-1/2">
					{listCaseTypes && (
						<InputList
							disabled={isDisabled}
							myDefaultValue={initialCaseType?.name}
							name="caseType"
							title="Case Type"
							register={register}
							classNameInput="!mt-2"
							classNameTitle="!text-[16px]"
							classNameLabel="!text-[16px]"
							options={listCaseTypes}
							setValueInput={handleSetValue}
							rules={applyRule ? rules.caseType : undefined}
							error={applyRule ? errors?.caseType : undefined}
						/>
					)}
				</div>
			</ContainerInputs>
			<ContainerInputs>
				<div className="flex-1 w-full lg:w-1/2">
					{listCaseStatus && (
						<InputList
							disabled={isDisabled}
							myDefaultValue={initialCaseStatus?.name}
							name="status"
							title="Case Status"
							classNameTitle="!text-[16px]"
							classNameLabel="!text-[16px]"
							register={register}
							classNameInput="!mt-2"
							options={listCaseStatus}
							setValueInput={handleSetValue}
							rules={applyRule ? rules.status : undefined}
							error={applyRule ? errors?.status : undefined}
						/>
					)}
				</div>
				<div className="flex-1 w-full lg:w-1/2">
					<InputListMultiple
						isSearch
						disabled={isDisabled}
						myDefaultValues={initialListStates}
						placeholder="Select State"
						title="Eligible State"
						name="elegibleStateIds"
						register={register}
						classNameTitle="!text-[16px]"
						maxSelectedDisplay={width < 1400 ? 2 : undefined}
						classNameInput="!mt-2"
						classNameIcon="!top-[38%]"
						setValueInput={handleSetValue}
						error={errors?.elegibleStateIds}
						rules={applyRule ? rules.elegibleStateIds : undefined}
						options={
							listStates
								? listStates.filter((state) => state.label !== 'Select')
								: []
						}
						onSelectAll={handleSelectAllStates}
						allSelected={allStatesSelected}
					/>
				</div>
			</ContainerInputs>
		</>
	);
};

export default CaseBasicInformation;
